/**
 * Slice-set validation, mirroring `validate_slices` in
 * utt-engine/src/auto_bot/slices.rs.
 *
 * The engine is the authority — it re-validates on save — but a config the
 * operator cannot see is wrong until the server rejects it is a bad editor.
 * The rules are small and stable enough that a mirror is worth the duplication;
 * the alternative is a round trip on every keystroke.
 *
 * If the two ever disagree, the ENGINE wins: it decides what actually trades.
 */

import type { ScoreSlice } from '@shared/types/autoBot';

export interface SliceIssue {
  /** Slice ids the message is about, so the editor can highlight those rows. */
  ids: string[];
  message: string;
}

/**
 * Every reason this slice set could not be saved, in reading order.
 *
 * DISABLED slices are excluded from coverage checks — switching a band off is
 * deliberate, and demanding the rest close the gap would make it impossible to
 * park one temporarily.
 */
export function validateSlices(slices: readonly ScoreSlice[]): SliceIssue[] {
  const issues: SliceIssue[] = [];
  const active = slices.filter((s) => s.enabled);

  if (active.length === 0) {
    return [{ ids: [], message: 'A bot needs at least one enabled slice' }];
  }

  for (const s of active) {
    if (s.maxScore !== null && s.minScore >= s.maxScore) {
      issues.push({
        ids: [s.id],
        message: `"${s.label}" has min ${s.minScore} at or above max ${s.maxScore}`,
      });
    }
    if (s.entry.kind === 'pullback') {
      if (!(s.entry.offsetPoints > 0)) {
        issues.push({
          ids: [s.id],
          message: `"${s.label}" rests its entry ${s.entry.offsetPoints} points away; must be above zero`,
        });
      }
      if (!(s.entry.expirySeconds > 0)) {
        issues.push({
          ids: [s.id],
          message: `"${s.label}" rests an entry with no expiry`,
        });
      }
    }
  }

  const unbounded = active.filter((s) => s.maxScore === null);
  if (unbounded.length > 1) {
    issues.push({
      ids: unbounded.map((s) => s.id),
      message: `"${unbounded[0].label}" and "${unbounded[1].label}" are both open-ended`,
    });
  }

  const sorted = [...active].sort((a, b) => a.minScore - b.minScore);
  for (let i = 0; i < sorted.length - 1; i++) {
    const lo = sorted[i];
    const hi = sorted[i + 1];
    if (lo.maxScore === null) {
      // An open-ended slice below another one swallows it whole.
      issues.push({
        ids: [lo.id, hi.id],
        message: `"${lo.label}" is open-ended and swallows "${hi.label}"`,
      });
      continue;
    }
    if (hi.minScore < lo.maxScore) {
      issues.push({
        ids: [lo.id, hi.id],
        message: `"${lo.label}" and "${hi.label}" both cover score ${hi.minScore}`,
      });
    } else if (hi.minScore > lo.maxScore) {
      issues.push({
        ids: [lo.id, hi.id],
        message: `Nothing covers ${lo.maxScore} to ${hi.minScore}, between "${lo.label}" and "${hi.label}"`,
      });
    }
  }

  return issues;
}

/** A label the operator did not have to invent, matching the migration's. */
export function autoLabel(minScore: number, maxScore: number | null): string {
  return maxScore === null ? `${minScore.toFixed(0)}+` : `${minScore.toFixed(0)}-${maxScore.toFixed(0)}`;
}
